import React from "react";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import { FaExternalLinkAlt, FaCertificate, FaGlobe, FaShieldAlt, FaNetworkWired, FaClock } from "react-icons/fa";

const ProfessionalCourses = () => {
  const professionalCourses = [
    {
      id: "comptia",
      title: "CompTIA",
      description: "Industry-leading IT certifications that validate your skills and knowledge in various technology domains. CompTIA certifications are recognized globally and help you advance your career in IT.",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop",
      features: [
        "A+ Certification for IT Fundamentals",
        "Network+ for Networking Skills", 
        "Security+ for Cybersecurity",
        "Cloud+ for Cloud Computing",
        "Project+ for Project Management"
      ],
      duration: "3-6 months",
      level: "Beginner to Advanced",
      externalLink: "https://www.comptia.org/en/"
    },
    {
      id: "cisco",
      title: "Cisco Networking Academy",
      description: "Comprehensive networking and cybersecurity education program that prepares students for careers in the digital economy. Learn from industry experts with hands-on experience.",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop",
      features: [
        "CCNA - Cisco Certified Network Associate",
        "CCNP - Cisco Certified Network Professional", 
        "CCIE - Cisco Certified Internetwork Expert",
        "Cybersecurity Fundamentals",
        "IoT and Digital Transformation"
      ],
      duration: "6-12 months",
      level: "Intermediate to Expert",
      externalLink: "https://www.netacad.com/"
    },
    {
      id: "ec-council",
      title: "EC-Council",
      description: "Leading provider of cybersecurity education, certification, and training. Specialized in ethical hacking, digital forensics, and cybersecurity management programs.",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop",
      features: [
        "CEH - Certified Ethical Hacker",
        "C|CISO - Chief Information Security Officer",
        "CHFI - Computer Hacking Forensic Investigator",
        "C|ND - Certified Network Defender",
        "C|PENT - Penetration Testing Professional"
      ],
      duration: "3-9 months",
      level: "Intermediate to Expert",
      externalLink: "https://www.eccouncil.org/"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-custom-pink to-gold py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Professional Courses
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Industry-recognized certifications and training programs to advance your career in technology
          </p>
        </div>
      </div>

      {/* Courses Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Choose Your Professional Path
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our professional courses are designed in partnership with industry leaders to provide you with the most current and relevant skills.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {professionalCourses.map((course) => (
              <div key={course.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative">
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-custom-pink text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Professional
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <FaCertificate className="text-gold mr-2" />
                    <h3 className="text-2xl font-bold text-gray-800">{course.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {course.description}
                  </p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Key Certifications:</h4>
                    <ul className="space-y-1">
                      {course.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <span className="w-2 h-2 bg-gold rounded-full mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex justify-between items-center mb-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <FaClock className="mr-1" />
                      {course.duration}
                    </span>
                    <span className="flex items-center">
                      <FaShieldAlt className="mr-1" />
                      {course.level}
                    </span>
                  </div>

                  <a
                    href={course.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-custom-pink hover:bg-gold text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center"
                  >
                    Learn More
                    <FaExternalLinkAlt className="ml-2" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Why Choose Professional Certifications?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional certifications validate your expertise and open doors to new career opportunities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-custom-pink text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaGlobe className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Global Recognition</h3>
              <p className="text-gray-600">Industry-standard certifications recognized worldwide by employers and organizations.</p>
            </div>

            <div className="text-center">
              <div className="bg-custom-pink text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaNetworkWired className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Career Advancement</h3>
              <p className="text-gray-600">Boost your career prospects with specialized skills and knowledge in high-demand areas.</p>
            </div>

            <div className="text-center">
              <div className="bg-custom-pink text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaShieldAlt className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Expert Training</h3>
              <p className="text-gray-600">Learn from industry experts with hands-on experience and real-world scenarios.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfessionalCourses;
